import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { DecimalPipe } from '@angular/common';

/**
 * Drop-in, crash-proof replacement for Angular's built-in `number` pipe.
 *
 * The built-in `DecimalPipe` throws `InvalidPipeArgument` (Angular error
 * NG02100) whenever it receives a value it cannot convert to a number — e.g. a
 * non-numeric string, an object, or `NaN`. A single bad value crashes the whole
 * view and floods production error logs (the same class of failure we hit with
 * dates — see SafeDatePipe).
 *
 * `safeNumber` delegates formatting to `DecimalPipe` (so output is identical for
 * valid inputs) but returns '' for null/empty/invalid values instead of
 * throwing. Standardise number rendering on this pipe so NG02100 from numeric
 * values cannot recur.
 *
 * Usage is identical to `number`:  {{ value | safeNumber:'1.2-2' }}
 */
@Pipe({
  name: 'safeNumber',
  standalone: true,
})
export class SafeNumberPipe implements PipeTransform {
  private readonly decimalPipe: DecimalPipe;

  constructor(@Inject(LOCALE_ID) locale: string) {
    this.decimalPipe = new DecimalPipe(locale);
  }

  transform(value: any, digitsInfo?: string, locale?: string): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    try {
      return this.decimalPipe.transform(value, digitsInfo, locale) ?? '';
    } catch {
      // Invalid/non-numeric value — render blank rather than throwing NG02100.
      return '';
    }
  }
}
